import React, { useState, useEffect, ChangeEvent, FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { recruitingAPI } from "../services/recruitingApi";

interface AdditionalContact {
  name?: string;
  email?: string;
  phone?: string;
  role?: string;
}

interface CustomerData {
  id?: string | number;
  name?: string;
  company_name?: string;
  email?: string;
  phone?: string;
  address?: string;
  website?: string;
  industry?: string;
  notes?: string;
  status?: string;
  company_size?: string;
  description?: string;
  employee_count?: string | number;
  founded_year?: string | number;
  tax_id?: string;
  company_type?: string;
  additional_contacts?: string | AdditionalContact[];
}

interface CustomerFormProps {
  customer?: CustomerData | null;
  onSave: () => void;
  onCancel: () => void;
  onDelete?: (customerId: string | number) => void;
}

interface FormData {
  name: string;
  company_name: string;
  email: string;
  phone: string;
  address: string;
  website: string;
  industry: string;
  notes: string;
  status: string;
  company_size: string;
  description: string;
  employee_count: string;
  founded_year: string;
  tax_id: string;
  company_type: string;
  additional_contacts: AdditionalContact[];
  [key: string]: string | number | AdditionalContact[];
}

interface FormErrors {
  [key: string]: string;
}

interface Note {
  id: number;
  content: string;
  author_name?: string;
  created_at: string;
}

interface NotesResponse {
  data?: Note[];
}

const CustomerForm: React.FC<CustomerFormProps> = ({
  customer,
  onSave,
  onCancel,
  onDelete,
}) => {
  const { t } = useTranslation(["recruiting", "common"]);
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<FormData>({
    name: "",
    company_name: "",
    email: "",
    phone: "",
    address: "",
    website: "",
    industry: "",
    notes: "",
    status: "active",
    company_size: "",
    description: "",
    employee_count: "",
    founded_year: "",
    tax_id: "",
    company_type: "",
    additional_contacts: [],
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [newNote, setNewNote] = useState("");
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editingNoteContent, setEditingNoteContent] = useState("");
  const [showNotesSection, setShowNotesSection] = useState(false);

  // Fetch customer notes if customer exists
  const { data: notesData } = useQuery<NotesResponse>(
    ["customer-notes", customer?.id],
    () =>
      recruitingAPI.getNotes(
        "customer",
        customer!.id as number,
      ) as Promise<NotesResponse>,
    {
      enabled: !!customer?.id,
    },
  );

  const notes = notesData?.data || [];

  // Initialize form data when customer prop changes
  useEffect(() => {
    if (customer) {
      // Handle additional_contacts - could be array or JSON string
      let additionalContacts: AdditionalContact[] = [];
      if (customer.additional_contacts) {
        if (Array.isArray(customer.additional_contacts)) {
          additionalContacts = customer.additional_contacts;
        } else if (typeof customer.additional_contacts === "string") {
          try {
            additionalContacts = JSON.parse(customer.additional_contacts);
          } catch (e) {
            console.warn("Failed to parse additional_contacts JSON:", e);
            additionalContacts = [];
          }
        }
      }

      setFormData({
        name: customer.name || "",
        company_name: customer.company_name || "",
        email: customer.email || "",
        phone: customer.phone || "",
        address: customer.address || "",
        website: customer.website || "",
        industry: customer.industry || "",
        notes: customer.notes || "",
        status: customer.status || "active",
        company_size: customer.company_size || "",
        description: customer.description || "",
        employee_count: customer.employee_count?.toString() || "",
        founded_year: customer.founded_year?.toString() || "",
        tax_id: customer.tax_id || "",
        company_type: customer.company_type || "",
        additional_contacts: additionalContacts,
      });
    } else {
      // Reset form when no customer
      setFormData({
        name: "",
        company_name: "",
        email: "",
        phone: "",
        address: "",
        website: "",
        industry: "",
        notes: "",
        status: "active",
        company_size: "",
        description: "",
        employee_count: "",
        founded_year: "",
        tax_id: "",
        company_type: "",
        additional_contacts: [],
      });
    }
  }, [customer]);

  // Create/Update customer mutation
  const saveCustomerMutation = useMutation<
    unknown,
    Error,
    Record<string, unknown>
  >(
    async (data) => {
      if (customer) {
        return await recruitingAPI.updateCustomer(
          customer.id as string | number,
          data,
        );
      } else {
        return await recruitingAPI.createCustomer(data);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("customers");
        onSave();
      },
      onError: (error: Error) => {
        console.error("Error saving customer:", error);
        setErrors({ general: t("customers.form.validation.saveFailed") });
      },
    },
  );

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value, type } = e.target;
    let newValue: string | number = value;
    if (type === "number") {
      newValue = value === "" ? "" : Number.parseInt(value, 10);
    }
    setFormData((prev) => ({ ...prev, [name]: newValue }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleAdditionalContactChange = (
    index: number,
    field: keyof AdditionalContact,
    value: string,
  ) => {
    setFormData((prev) => {
      const contacts = [...prev.additional_contacts];
      if (!contacts[index]) {
        contacts[index] = { name: "", email: "", phone: "", role: "" };
      }
      contacts[index][field] = value;
      return { ...prev, additional_contacts: contacts };
    });
  };

  const addAdditionalContact = () => {
    setFormData((prev) => ({
      ...prev,
      additional_contacts: [
        ...prev.additional_contacts,
        { name: "", email: "", phone: "", role: "" },
      ],
    }));
  };

  const removeAdditionalContact = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      additional_contacts: prev.additional_contacts.filter(
        (_, i) => i !== index,
      ),
    }));
  };

  // Notes mutations
  const createNoteMutation = useMutation<unknown, Error, string>(
    async (noteContent) => {
      return await recruitingAPI.createNote({
        entity_type: "customer",
        entity_id: customer!.id as number,
        content: noteContent,
      });
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries(["customer-notes", customer?.id]);
        setNewNote("");
      },
    },
  );

  const updateNoteMutation = useMutation<
    unknown,
    Error,
    { noteId: number; content: string }
  >(
    async ({ noteId, content }) => {
      return await recruitingAPI.updateNote(noteId, { content });
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries(["customer-notes", customer?.id]);
        setEditingNoteId(null);
        setEditingNoteContent("");
      },
    },
  );

  const deleteNoteMutation = useMutation<unknown, Error, number>(
    async (noteId) => {
      return await recruitingAPI.deleteNote(noteId);
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries(["customer-notes", customer?.id]);
      },
    },
  );

  const handleAddNote = () => {
    if (newNote.trim() && customer?.id) {
      createNoteMutation.mutate(newNote);
    }
  };

  const handleStartEditNote = (note: Note) => {
    setEditingNoteId(note.id);
    setEditingNoteContent(note.content);
  };

  const handleCancelEditNote = () => {
    setEditingNoteId(null);
    setEditingNoteContent("");
  };

  const handleSaveEditNote = () => {
    if (editingNoteContent.trim() && editingNoteId) {
      updateNoteMutation.mutate({
        noteId: editingNoteId,
        content: editingNoteContent,
      });
    }
  };

  const handleDeleteNote = (noteId: number) => {
    if (window.confirm(t("customers.form.confirmDeleteNote"))) {
      deleteNoteMutation.mutate(noteId);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim() && !formData.company_name.trim()) {
      newErrors.name = t("customers.form.validation.nameOrCompanyRequired");
    }

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t("customers.form.validation.emailInvalid");
    }

    if (formData.website && !formData.website.startsWith("http")) {
      newErrors.website = t("customers.form.validation.websiteInvalid");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Prepare data for submission - convert additional_contacts to JSON string
    const submitData: Record<string, unknown> = {
      ...formData,
      additional_contacts:
        formData.additional_contacts.length > 0
          ? JSON.stringify(formData.additional_contacts)
          : null,
      employee_count: formData.employee_count
        ? Number.parseInt(formData.employee_count, 10)
        : null,
      founded_year: formData.founded_year
        ? Number.parseInt(formData.founded_year, 10)
        : null,
    };

    saveCustomerMutation.mutate(submitData);
  };

  const handleDelete = () => {
    if (
      customer &&
      customer.id &&
      window.confirm(
        t("customers.form.confirmDelete", {
          name: customer.company_name || customer.name,
        }),
      )
    ) {
      onDelete?.(customer.id);
    }
  };

  return (
    <div className="customer-form">
      <div className="form-header">
        <h2>
          {customer
            ? t("customers.form.titleEdit")
            : t("customers.form.titleNew")}
        </h2>
        <div className="form-actions">
          {customer && (
            <button type="button" onClick={handleDelete} className="btn-delete">
              {t("common:actions.delete")}
            </button>
          )}
          <button type="button" onClick={onCancel} className="btn-secondary">
            {t("common:actions.cancel")}
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="form-content">
        {errors.general && (
          <div className="error-message general-error">{errors.general}</div>
        )}

        {/* Basic Information */}
        <div className="form-section">
          <h3>{t("customers.form.sections.basicInformation")}</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">
                {t("customers.form.fields.contactName")}
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={errors.name ? "error" : ""}
                placeholder={t("customers.form.placeholders.contactName")}
              />
              {errors.name && <span className="error-text">{errors.name}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="company_name">
                {t("customers.form.fields.companyName")}
              </label>
              <input
                type="text"
                id="company_name"
                name="company_name"
                value={formData.company_name}
                onChange={handleInputChange}
                placeholder={t("customers.form.placeholders.companyName")}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">{t("customers.form.fields.email")}</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={errors.email ? "error" : ""}
                placeholder={t("customers.form.placeholders.email")}
              />
              {errors.email && (
                <span className="error-text">{errors.email}</span>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="phone">{t("customers.form.fields.phone")}</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder={t("customers.form.placeholders.phone")}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="website">
                {t("customers.form.fields.website")}
              </label>
              <input
                type="url"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                className={errors.website ? "error" : ""}
                placeholder={t("customers.form.placeholders.website")}
              />
              {errors.website && (
                <span className="error-text">{errors.website}</span>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="industry">
                {t("customers.form.fields.industry")}
              </label>
              <input
                type="text"
                id="industry"
                name="industry"
                value={formData.industry}
                onChange={handleInputChange}
                placeholder={t("customers.form.placeholders.industry")}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="address">
              {t("customers.form.fields.address")}
            </label>
            <textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              rows={3}
              placeholder={t("customers.form.placeholders.address")}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="status">
                {t("customers.form.fields.status")}
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
              >
                <option value="active">
                  {t("customers.form.status.active")}
                </option>
                <option value="inactive">
                  {t("customers.form.status.inactive")}
                </option>
              </select>
            </div>
          </div>
        </div>

        {/* Company Details */}
        <div className="form-section">
          <h3>{t("customers.form.sections.companyDetails")}</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="company_size">
                {t("customers.form.fields.companySize")}
              </label>
              <select
                id="company_size"
                name="company_size"
                value={formData.company_size}
                onChange={handleInputChange}
              >
                <option value="">
                  {t("customers.form.companySize.selectSize")}
                </option>
                <option value="small">
                  {t("customers.form.companySize.small")}
                </option>
                <option value="medium">
                  {t("customers.form.companySize.medium")}
                </option>
                <option value="large">
                  {t("customers.form.companySize.large")}
                </option>
                <option value="enterprise">
                  {t("customers.form.companySize.enterprise")}
                </option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="employee_count">
                {t("customers.form.fields.employeeCount")}
              </label>
              <input
                type="number"
                id="employee_count"
                name="employee_count"
                value={formData.employee_count}
                onChange={handleInputChange}
                min="0"
                placeholder={t("customers.form.placeholders.employeeCount")}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="founded_year">
                {t("customers.form.fields.foundedYear")}
              </label>
              <input
                type="number"
                id="founded_year"
                name="founded_year"
                value={formData.founded_year}
                onChange={handleInputChange}
                min="1800"
                max={new Date().getFullYear()}
                placeholder={t("customers.form.placeholders.foundedYear")}
              />
            </div>
            <div className="form-group">
              <label htmlFor="company_type">
                {t("customers.form.fields.companyType")}
              </label>
              <input
                type="text"
                id="company_type"
                name="company_type"
                value={formData.company_type}
                onChange={handleInputChange}
                placeholder={t("customers.form.placeholders.companyType")}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="tax_id">{t("customers.form.fields.taxId")}</label>
              <input
                type="text"
                id="tax_id"
                name="tax_id"
                value={formData.tax_id}
                onChange={handleInputChange}
                placeholder={t("customers.form.placeholders.taxId")}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description">
              {t("customers.form.fields.description")}
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={5}
              placeholder={t("customers.form.placeholders.description")}
            />
          </div>
        </div>

        {/* Additional Contacts */}
        <div className="form-section">
          <h3>{t("customers.form.sections.additionalContacts")}</h3>
          {formData.additional_contacts.map((contact, index) => (
            <div key={index} className="additional-contact-item">
              <div className="form-row">
                <div className="form-group">
                  <label>{t("customers.form.fields.name")}</label>
                  <input
                    type="text"
                    value={contact.name || ""}
                    onChange={(e) =>
                      handleAdditionalContactChange(
                        index,
                        "name",
                        e.target.value,
                      )
                    }
                    placeholder={t("customers.form.placeholders.contactName")}
                  />
                </div>
                <div className="form-group">
                  <label>{t("customers.form.fields.email")}</label>
                  <input
                    type="email"
                    value={contact.email || ""}
                    onChange={(e) =>
                      handleAdditionalContactChange(
                        index,
                        "email",
                        e.target.value,
                      )
                    }
                    placeholder={t("customers.form.placeholders.email")}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>{t("customers.form.fields.phone")}</label>
                  <input
                    type="tel"
                    value={contact.phone || ""}
                    onChange={(e) =>
                      handleAdditionalContactChange(
                        index,
                        "phone",
                        e.target.value,
                      )
                    }
                    placeholder={t("customers.form.placeholders.phone")}
                  />
                </div>
                <div className="form-group">
                  <label>{t("customers.form.fields.role")}</label>
                  <input
                    type="text"
                    value={contact.role || ""}
                    onChange={(e) =>
                      handleAdditionalContactChange(
                        index,
                        "role",
                        e.target.value,
                      )
                    }
                    placeholder={t("customers.form.placeholders.contactRole")}
                  />
                </div>
                <div className="form-group">
                  <label>&nbsp;</label>
                  <button
                    type="button"
                    onClick={() => removeAdditionalContact(index)}
                    className="btn-delete btn-small"
                  >
                    {t("common:actions.remove")}
                  </button>
                </div>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={addAdditionalContact}
            className="btn btn-secondary"
          >
            {t("customers.form.buttons.addContact")}
          </button>
        </div>

        {/* Notes */}
        <div className="form-section">
          <h3>{t("customers.form.sections.notes")}</h3>
          <div className="form-group">
            <label htmlFor="notes">{t("customers.form.fields.notes")}</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={5}
              placeholder={t("customers.form.placeholders.notes")}
            />
          </div>
        </div>

        {/* Company Comments/Notes Section */}
        {customer && (
          <div className="form-section">
            <div
              className="section-header-toggle"
              onClick={() => setShowNotesSection(!showNotesSection)}
            >
              <h3>{t("customers.form.sections.companyComments")}</h3>
              <span>{showNotesSection ? "\u25BC" : "\u25B6"}</span>
            </div>
            {showNotesSection && (
              <div className="notes-section-content">
                <div className="add-note-section">
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder={t("customers.form.placeholders.addComment")}
                    className="note-textarea"
                    rows={3}
                  />
                  <button
                    type="button"
                    onClick={handleAddNote}
                    className="btn-primary"
                    disabled={!newNote.trim() || createNoteMutation.isLoading}
                  >
                    {createNoteMutation.isLoading
                      ? t("common:actions.adding")
                      : t("customers.form.buttons.addComment")}
                  </button>
                </div>

                <div className="notes-list">
                  {notes.length === 0 ? (
                    <p className="muted-text">
                      {t("customers.form.noComments")}
                    </p>
                  ) : (
                    notes.map((note) => (
                      <div key={note.id} className="note-item">
                        {editingNoteId === note.id ? (
                          <div className="note-edit">
                            <textarea
                              value={editingNoteContent}
                              onChange={(e) =>
                                setEditingNoteContent(e.target.value)
                              }
                              className="note-textarea"
                              rows={3}
                            />
                            <div className="note-edit-actions">
                              <button
                                type="button"
                                onClick={handleSaveEditNote}
                                className="btn-primary btn-small"
                                disabled={
                                  !editingNoteContent.trim() ||
                                  updateNoteMutation.isLoading
                                }
                              >
                                {t("common:actions.save")}
                              </button>
                              <button
                                type="button"
                                onClick={handleCancelEditNote}
                                className="btn btn-secondary btn-small"
                              >
                                {t("common:actions.cancel")}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="note-content">
                            <p>{note.content}</p>
                            <div className="note-meta">
                              <span className="note-author">
                                {note.author_name ||
                                  t("customers.form.unknown")}
                              </span>
                              <span className="note-date">
                                {new Date(note.created_at).toLocaleDateString()}{" "}
                                {new Date(note.created_at).toLocaleTimeString()}
                              </span>
                              <div className="note-actions">
                                <button
                                  type="button"
                                  onClick={() => handleStartEditNote(note)}
                                  className="btn-link"
                                  title={t("common:actions.edit")}
                                >
                                  {t("common:actions.edit")}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteNote(note.id)}
                                  className="btn-link"
                                  title={t("common:actions.delete")}
                                >
                                  {t("common:actions.delete")}
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Form Actions */}
        <div className="form-actions-bottom">
          <button
            type="submit"
            className="btn-primary"
            disabled={saveCustomerMutation.isLoading}
          >
            {saveCustomerMutation.isLoading && t("common:actions.saving")}
            {!saveCustomerMutation.isLoading &&
              customer &&
              t("customers.form.buttons.updateCustomer")}
            {!saveCustomerMutation.isLoading &&
              !customer &&
              t("customers.form.buttons.createCustomer")}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-secondary"
          >
            {t("common:actions.cancel")}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CustomerForm;
